import { useState } from 'react';
import { useRouter } from 'next/router';

import { Alert, Anchor, Box, Divider, Heading, Icon, List, Paragraph, Tag } from '@zillow/constellation-10';
import { IconAIMagicFilled } from '@zillow/constellation-icons';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useAiGeneratedFields } from '../../../../hooks/queries/useAiGeneratedFields';
import accessUtils from '../../../../utils/accessUtils';

import { EXTERNAL_URLS } from '../../../../constants/urls';
import { VIEW_TYPE } from '../../../WizardTipCardContainer/constants';

import AiDraftFailedModal from './components/AiDraftFailedModal';
import AiDraftingState from './components/AiDraftingState';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import AmenitiesInteriorFormField from '../../../PostingPath/AmenitiesInteriorFormField';
import AmenitiesWithAdditionalAmenitiesFields from '../../../PostingPath/AmenitiesWithAdditionalAmenitiesFields';
import PropertyDescriptionFormField from '../../../PostingPath/PropertyDescriptionFormField';
import PageWizard from '../../../Wizard/PageWizard';
import { WizardTip } from '../../../WizardTipCardContainer/WizardTip';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const DescriptionAndAmenities = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { status: aiStatus, propertyFields: propertyFieldsData } = useAiGeneratedFields();

  const [isDraftFailureDismissed, setIsDraftFailureDismissed] = useState(false);

  const shouldShowBrokerFeeAlert = accessUtils.shouldShowBrokerFeeAlert({
    state: listingDetails?.state ?? '',
  });

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div data-testid="DescriptionAndAmenities">
        <AiDraftFailedModal
          isOpen={aiStatus === 'failed' && !isDraftFailureDismissed}
          onDismiss={() => setIsDraftFailureDismissed(true)}
        />
        {aiStatus === 'waiting' ? (
          <AiDraftingState />
        ) : (
          <Box css={wizardFlexContainerStyles}>
            <Box data-testid="FormSectionContainer" css={wizardFormSectionContainerStyles.raw()}>
              {propertyFieldsData ? (
                <>
                  <Box css={{ display: 'flex', alignItems: 'center', gap: '400', marginBottom: '400' }}>
                    <Heading textStyle="heading-md" level={2}>
                      Your draft is ready
                    </Heading>
                    <Tag tone="purple" icon={<Icon render={<IconAIMagicFilled />} size="sm" />}>
                      AI assisted
                    </Tag>
                  </Box>
                  <Paragraph css={wizardParagraphStyles}>
                    Your property description was drafted using your photos, notes, and property data, and checked for
                    alignment with our{' '}
                    <Anchor href={EXTERNAL_URLS.PLEDGE} target="_blank" rel="noopener noreferrer">
                      Respectful Renting Pledge
                    </Anchor>
                    . Since AI can make mistakes, review everything carefully and make any necessary edits before
                    publishing your listing.
                  </Paragraph>
                </>
              ) : (
                <>
                  <Heading textStyle="heading-md" level={2} css={wizardHeadingStyles}>
                    Description & amenities
                  </Heading>
                  <Paragraph css={wizardParagraphStyles}>
                    Write several sentences describing upgrades, attractive amenities and other appealing details like
                    the property’s proximity to transit, dining, shopping, and other local attractions. Then select all
                    relevant included amenities.
                  </Paragraph>
                </>
              )}
              <Heading level={2} textStyle="heading-xs" css={wizardHeadingStyles}>
                Property description
              </Heading>
              {shouldShowBrokerFeeAlert && (
                <Alert tone="info" css={{ marginBottom: '600' }}>
                  <strong>MA Broker Fee Rule:</strong> Starting Aug 1, broker fees can be charged only to tenants who
                  directly hire a broker
                </Alert>
              )}
              <PropertyDescriptionFormField showLabel={false} aiDescription={propertyFieldsData?.description?.value} />
              {propertyFieldsData && (
                <Paragraph textStyle="body-sm-bold" css={{ marginTop: '400' }}>
                  This draft was checked for alignment with our{' '}
                  <Anchor href={EXTERNAL_URLS.PLEDGE} target="_blank" rel="noopener noreferrer">
                    Respectful Renting Pledge
                  </Anchor>
                  .
                </Paragraph>
              )}
              <Box css={{ marginTop: '800' }}>
                <WizardTip header="Tips for property descriptions" viewType={VIEW_TYPE.ACCORDION}>
                  <List.Root appearance="bullet">
                    <List.Item>
                      Market the property’s proximity to transit, dining, shopping, and other local attractions
                    </List.Item>
                    <List.Item>Mention upgrades, attractive amenities, and other appealing details</List.Item>
                    <List.Item>Indicate whether you live on-site</List.Item>
                  </List.Root>
                </WizardTip>
              </Box>

              <Divider css={{ marginBlock: '600' }} />
              <Heading textStyle="heading-sm" level={2} css={wizardHeadingStyles}>
                Interior amenities
              </Heading>
              <AmenitiesInteriorFormField aiAmenityFields={propertyFieldsData?.amenityFields} />
              <Divider css={{ marginBlock: '600' }} />
              <Heading textStyle="heading-sm" level={2} css={wizardHeadingStyles}>
                Property amenities
              </Heading>
              <AmenitiesWithAdditionalAmenitiesFields aiAmenityFields={propertyFieldsData?.amenityFields} />
            </Box>
          </Box>
        )}
      </div>
    </PageWizard>
  );
};

export default DescriptionAndAmenities;
